import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AmbulanzType,
  ClientDetailResponse,
  ContactDetailType,
  CreateAmbulanzRequest,
  ambulanzesApi,
  clientsApi,
} from "@/api";
import { AmbulanzFormValues } from "@/common/components/ambulanzes/shared/AmbulanzForm.types";
import { useAmbulanzFormOptions } from "@/common/components/ambulanzes/shared/hooks/useAmbulanzFormOptions";
import { firstStoredContact } from "@/common/components/appointments/client-appointment-creation/ClientAppointmentCreation.utils";
import { emptyToNull } from "@/common/helper/commonHelper";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useAmbulanzCreation(onClose: () => void, client?: ClientDetailResponse) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const { typeOptions, validationSchema } = useAmbulanzFormOptions();

  const { data: contacts } = useQuery({
    queryKey: [QueryKey.ClientPersonalData, client?.clientId],
    queryFn: client ? () => clientsApi.getClientPersonalData({ clientId: client.clientId }) : skipToken,
    select: (data) => ({
      phone: firstStoredContact(data.contactDetails, ContactDetailType.Phone),
      email: firstStoredContact(data.contactDetails, ContactDetailType.Email),
    }),
  });

  const createAmbulanzMutation = useMutation({
    mutationFn: (request: CreateAmbulanzRequest) => ambulanzesApi.createAmbulanz({ createAmbulanzRequest: request }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Ambulanzes] });
      showSuccess(t("ambulanzes.creationDialog.successfullyAdded"));
      onClose();
    },
  });

  const formik = useFormikWithDirtyForm<AmbulanzFormValues>({
    initialValues: {
      type: AmbulanzType.Ambulanz,
      ambulanzNumber: "",
      clientId: client?.clientId ?? null,
      clientName: null,
      phone: contacts?.phone ?? "",
      email: contacts?.email ?? "",
      note: "",
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const isAmbulanz = values.type === AmbulanzType.Ambulanz;

      const request: CreateAmbulanzRequest = {
        type: values.type,
        ambulanzNumber: isAmbulanz ? emptyToNull(values.ambulanzNumber) : null,
        clientId: values.clientId,
        // Free-text name is only persisted when the activity is not linked to a client record.
        clientName: values.clientId ? null : emptyToNull(values.clientName ?? ""),
        phone: emptyToNull(values.phone),
        email: emptyToNull(values.email),
        note: emptyToNull(values.note),
      };

      try {
        await createAmbulanzMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  // Whitespace counts as empty because the backend rejects it, though the Yup rule would let it through.
  const { type, ambulanzNumber } = formik.values;
  const mandatoryFieldsFilled = type !== AmbulanzType.Ambulanz || ambulanzNumber.trim() !== "";
  const submitDisabled = !mandatoryFieldsFilled || createAmbulanzMutation.isPending;

  return { formik, typeOptions, submitDisabled };
}
